import { Controller } from "stimulus";
import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import BubbleMenu from '@tiptap/extension-bubble-menu';
import Placeholder from '@tiptap/extension-placeholder';
import { EpisodeReference, EpisodeMentionsFeature } from "utils/tiptap_episode_mentions";
import { EXTENSION_REGISTRY, SlashMenu, BlockIds } from "utils/tiptap_extensions";
import { attachmentUploadSlashItem } from "utils/tiptap_extensions/uploads";
import { formattingSlashItems } from "utils/tiptap_extensions/formatting";

export default class extends Controller {
  static targets = ["editor", "input", "toolbar", "fileInput", "plusButton", "formatDropdown"];
  static values = {
    bubble: Boolean,
    placeholder: String,
    allowImages: Boolean,
    episodeMentions: Boolean,
    extensions: Array, // list of rich-content extension names to enable, e.g. ["callout"]
    blockCapabilities: Object, // per-post facts a block extension needs, keyed by extension name, e.g. { chapters: { available: true } }
    requireBody: Boolean,
  }
  
  async connect() {
    // Attach these before the async gap below so they're active while `this.editor` doesn't exist yet.
    this.form = this.element.closest("form");
    this._formDataHandler = (event) => this.syncBodyIntoFormData(event);
    this._submitGuardHandler = (event) => this.guardEmptySubmit(event);
    this.form?.addEventListener("formdata", this._formDataHandler);
    this.form?.addEventListener("submit", this._submitGuardHandler);

    const templates = await this.fetchExtensionTemplates();

    // Initialize the editor
    this.extensions = [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'underline',
        },
      }),
    ];

    this.initializeEpisodeMentions();
    this.initializeSlashMenu(templates);
    this.initializePlaceholder();
    this.initializeBubbleMenu();

    const blockImageOrFilePaste = (view, event) => {
      if (this.allowImages()) return false;

      const clipboardData = event.clipboardData;
      if (!clipboardData) return false;

      if (clipboardData.files && clipboardData.files.length > 0) {
        event.preventDefault();
        return true;
      }

      const html = clipboardData.getData('text/html');
      if (html && /(<img\b[^>]*>|<figure\b[^>]*>)/i.test(html)) {
        event.preventDefault();

        const text = clipboardData.getData('text/plain');
        if (text) {
          view.dispatch(view.state.tr.insertText(text));
        }

        return true;
      }

      return false;
    };

    const blockImageOrFileDrop = (view, event) => {
      if (this.allowImages()) return false;

      const dataTransfer = event.dataTransfer;
      if (!dataTransfer) return false;

      if (dataTransfer.files && dataTransfer.files.length > 0) {
        event.preventDefault();
        return true;
      }

      const html = dataTransfer.getData('text/html');
      if (html && /(<img\b[^>]*>|<figure\b[^>]*>)/i.test(html)) {
        event.preventDefault();
        return true;
      }

      return false;
    };

    console.log('this.extensions', this.extensions);

    this.editor = new Editor({
      element: this.editorTarget,
      extensions: this.extensions,
      editable: true,
      parseOptions: {
        preserveWhitespace: "full",
      },
      content: this.inputTarget.value || '',
      onUpdate: ({ editor }) => {
        // Update the hidden input with HTML content when editor changes
        this.inputTarget.value = editor.getHTML();

        // Dispatch input event to trigger any form listeners (like auto-save)
        const event = new Event('input', { bubbles: true });
        this.inputTarget.dispatchEvent(event);

        this.episodeMentionsFeature?.onUpdate();
      },
      onBlur: () => {
        // Dispatch change event to trigger any form listeners (like auto-save)
        const event = new Event('change', { bubbles: true });
        this.inputTarget.dispatchEvent(event);

        this.episodeMentionsFeature?.onBlur();
      },
      onSelectionUpdate: () => {
        this.episodeMentionsFeature?.onSelectionUpdate();
      },
      editorProps: {
        attributes: {
          class: 'trix-content focus:outline-none max-w-none',
        },
        handlePaste: (view, event) => {
          if (blockImageOrFilePaste(view, event)) return true;
          return this.episodeMentionsFeature?.handlePaste(view, event) || false;
        },
        handleDrop: (view, event) => blockImageOrFileDrop(view, event),
        handleKeyDown: (view, event) => this.episodeMentionsFeature?.handleKeyDown(view, event) || false,
      },
    });

    this.episodeMentionsFeature?.setEditor(this.editor);

    this._mouseMoveHandler = (e) => {
      if (!this.hasPlusButtonTarget || !this.editor) return;
      const view = this.editor.view;
      const editorDomRect = view.dom.getBoundingClientRect();

      if (e.clientY < editorDomRect.top || e.clientY > editorDomRect.bottom) {
        this.hidePlusButton();
        return;
      }

      const pos = view.posAtCoords({ left: editorDomRect.left + 1, top: e.clientY });
      if (!pos) return;

      // Walk up to the nearest textblock so the button snaps to a line, not a character boundary
      const $pos = this.editor.state.doc.resolve(pos.pos);
      let depth = $pos.depth;
      while (depth > 0 && !$pos.node(depth).isTextblock) depth--;
      if (depth === 0) return;

      const coords = view.coordsAtPos(Math.max(1, $pos.start(depth)));
      const containerRect = this.element.getBoundingClientRect();
      this.plusButtonTarget.style.top = `${(coords.top + coords.bottom) / 2 - containerRect.top}px`;
      this.plusButtonTarget.classList.remove('hidden');
    };
    this._mouseLeaveHandler = () => this.hidePlusButton();
    this.element.addEventListener('mousemove', this._mouseMoveHandler);
    this.element.addEventListener('mouseleave', this._mouseLeaveHandler);
  }

  initializeBubbleMenu() {
    const enableBubble = this.hasBubbleValue ? this.bubbleValue : true;

    if (enableBubble) {
      this.extensions.push(
        BubbleMenu.configure({
          element: this.toolbarTarget,
          shouldShow: ({ editor, state }) => {
            return editor.isActive('link') || state.selection.content().size > 0;
          },
        })
      );
    }
  }

  initializeEpisodeMentions() {
    const enableEpisodeMentions = this.hasEpisodeMentionsValue ? this.episodeMentionsValue : false;
    this.episodeMentionsFeature = enableEpisodeMentions ? new EpisodeMentionsFeature({ hostElement: this.element }) : null;

    if (enableEpisodeMentions) {
      this.extensions.push(EpisodeReference);
    }
  }


  async fetchExtensionTemplates() {
    const enabled = this.hasExtensionsValue ? this.extensionsValue : [];
    const templateMap = {};
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    await Promise.all(
      EXTENSION_REGISTRY
        .filter(({ name, serverTemplates }) => enabled.includes(name) && serverTemplates)
        .map(async ({ name, templatePath }) => {
          try {
            const res = await fetch(templatePath, {
              headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken },
              credentials: 'include',
            });
            if (res.ok) templateMap[name] = await res.json();
          } catch (_) {
            // proceed without templates if fetch fails
          }
        })
    );

    return templateMap;
  }

  initializeSlashMenu(templates = {}) {
    const enabledExtensions = this.hasExtensionsValue ? this.extensionsValue : [];
    const slashItems = [];
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    let blockIdsAdded = false;

    for (const { name, extension, slashItem, renderPath, block } of EXTENSION_REGISTRY) {
      if (enabledExtensions.includes(name)) {
        const config = { templates: templates[name] ?? null };
        if (this.hasBlockCapabilitiesValue && this.blockCapabilitiesValue[name]) {
          config.capabilities = this.blockCapabilitiesValue[name];
        }

        if (renderPath) {
          config.fetchRenderedHtml = async (attrs) => {
            try {
              const toSnakeCase = (str) => str.replace(/[A-Z]/g, c => `_${c.toLowerCase()}`);
              const params = new URLSearchParams(
                Object.fromEntries(Object.entries(attrs).map(([k, v]) => [toSnakeCase(k), v]))
              );
              const res = await fetch(`${renderPath}?${params}`, {
                headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken },
                credentials: 'include',
              });
              if (res.ok) {
                const data = await res.json();
                return data.rendered_html ?? null;
              }
            } catch (_) { /* proceed without rendered HTML if fetch fails */ }
            return null;
          };
          config.onRenderedHtmlFetched = () => {
            if (!this.editor) return;
            this.inputTarget.value = this.editor.getHTML();
            const event = new Event('input', { bubbles: true });
            this.inputTarget.dispatchEvent(event);
          };
        }

        this.extensions.push(extension.configure(config));
        if (block && !blockIdsAdded) {
          this.extensions.push(BlockIds);
          blockIdsAdded = true;
        }
        if (slashItem) slashItems.push(slashItem);
      }
    }

    slashItems.push(...formattingSlashItems);
    
    const attachmentUpload = document.createElement('input');
    attachmentUpload.type = 'file';
    attachmentUpload.name = 'attachment';
    attachmentUpload.accept = 'application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.presentationml.presentation';
    attachmentUpload.style.display = 'none';
    attachmentUpload.dataset.tipTapTarget = 'fileInput';
    attachmentUpload.dataset.action = 'change->tiptap#handleFileUpload';
    this.element.appendChild(attachmentUpload);
    slashItems.push(attachmentUploadSlashItem);

    if (slashItems.length > 0) {
      this.extensions.push(SlashMenu.configure({ items: slashItems }));
    }

    if (this.allowImages()) {
      const imageUpload = document.createElement('input');
      imageUpload.type = 'file';
      imageUpload.name = 'image';
      imageUpload.accept = 'image/*';
      imageUpload.style.display = 'none';
      imageUpload.dataset.tipTapTarget = 'fileInput';
      imageUpload.dataset.action = 'change->tiptap#handleFileUpload';
      this.element.appendChild(imageUpload);

      this.extensions.push(
        Image.configure({
          allowBase64: false, // Don't allow base64 images, we'll handle image uploads
          inline: false,
          HTMLAttributes: {
            width: '600', // Fixing the width like this is important for emails
            height: 'auto',
            class: 'block mx-auto',
          },
        })
      );
    }
  }

  initializePlaceholder() {
    this.extensions.push(
      Placeholder.configure({
        placeholder: ({ node }) => {
          if (node.type.name === 'callout') return null;
          if (node.type.name === 'heading') {
            return node.attrs.level === 1 ? 'Heading' : 'Subheading';
          }
          if (node.type.name === 'blockquote') {
            return 'Quote';
          }
          return this.hasPlaceholderValue ? this.placeholderValue : "Write something, or press '/' for image, content gates, or other content";
        },
      })
    );
  }

  disconnect() {
    this.form?.removeEventListener("formdata", this._formDataHandler);
    this.form?.removeEventListener("submit", this._submitGuardHandler);
    this.element.removeEventListener('mousemove', this._mouseMoveHandler);
    this.element.removeEventListener('mouseleave', this._mouseLeaveHandler);
    this.episodeMentionsFeature?.destroy();
    this.episodeMentionsFeature = null;
    if (this.editor) {
      this.editor.destroy();
      this.editor = null;
    }
  }

  // `formdata` fires while FormData is being built for submission - the last point we can still write into the request body.
  syncBodyIntoFormData(event) {
    if (!this.editor) return;

    const html = this.editor.getHTML();
    this.inputTarget.value = html;
    event.formData.set(this.inputTarget.name, html);
  }

  // Blocks submission when the editor isn't ready or is empty (including Tiptap's empty-doc "<p></p>"); stopPropagation keeps rails-ujs from disabling the button for a submission that never happened.
  // Opt-in via the require-body value: most usages of this controller (post/thread descriptions) have no presence validation and must stay submittable empty.
  guardEmptySubmit(event) {
    if (!this.hasRequireBodyValue || !this.requireBodyValue) return;
    if (this.editor && !this.editor.isEmpty) return;

    event.preventDefault();
    event.stopPropagation();
  }

  focusEditor() {
    if (!this.editor) return;
    this.editor.commands.focus();
  }

  // Without this, mousedown on the submit button blurs the editor first, which can hide the
  // button (it's only shown via group-focus-within) before the resulting click ever fires.
  preventBlur(event) {
    event.preventDefault();
  }

  initiateUpload(imageOrAttachment) {
    const input = this.element.querySelector(`input[name="${imageOrAttachment}"]`);
    if (!input) return;
    input.click();
  }

  formatDropdownTargetConnected(el) {
    if (!this.editor) return;

    // Set the format dropdown to the current node type
    const nodeType = this.editor.state.selection.$head.parent.type.name;
    let value = nodeType;

    if (nodeType === 'heading') {
      const level = this.editor.state.selection.$head.parent.attrs.level;
      value = `heading${level}`;
    } else if (nodeType === 'paragraph') {
      // For lists and blockquote, we need to check ancestors since they're nested structures
      const isInBulletList = this.editor.isActive('bulletList');
      const isInOrderedList = this.editor.isActive('orderedList');
      const isInBlockquote = this.editor.isActive('blockquote');
    
      if (isInBulletList) {
        value = 'bulletList';
      } else if (isInOrderedList) {
        value = 'orderedList';
      } else if (isInBlockquote) {
        value = 'blockquote';
      } else {
        value = 'paragraph';
      }
    }

    // If the element doesn't have the value we want to set, default to paragraph
    // (FloatingMenu and Toolbar use different values for lists)
    if (!el.querySelector(`option[value="${value}"]`)) {
      value = 'paragraph';
    }

    el.value = value;
  }

  // Toolbar button handlers
  toggleBold() {
    this.editor.chain().focus().toggleBold().run();
  }

  toggleItalic() {
    this.editor.chain().focus().toggleItalic().run();
  }

  toggleUnderline() {
    this.editor.chain().focus().toggleUnderline().run();
  }

  toggleStrike() {
    this.editor.chain().focus().toggleStrike().run();
  }

  toggleHeading(event) {
    const level = parseInt(event.currentTarget.dataset.level || 1, 10);
    this.editor.chain().focus().toggleHeading({ level }).run();
  }

  toggleBulletList() {
    this.editor.chain().focus().toggleBulletList().run();
  }

  toggleOrderedList() {
    this.editor.chain().focus().toggleOrderedList().run();
  }
  
  toggleBlockquote() {
    this.editor.chain().focus().toggleBlockquote().run();
  }

  openSlashMenu(event) {
    event?.preventDefault();
    this.editor.chain().focus().insertContent('/').run();
  }

  hidePlusButton() {
    if (!this.hasPlusButtonTarget) return;
    this.plusButtonTarget.classList.add('hidden');
  }

  allowImages() {
    return this.hasAllowImagesValue ? this.allowImagesValue : true;
  }

  setLink() {
    const previousUrl = this.editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);
    
    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === '') {
      this.editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    // update link
    this.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }

  createUploadIndicator(filename) {
    const indicator = document.createElement('div');
    indicator.className = 'upload-indicator';
    indicator.innerHTML = `
      <div class="upload-indicator-progress"></div>
      <div class="upload-status">Uploading ${filename}...</div>
    `;
    return indicator;
  }

  // File upload handler for images
  // Format dropdown handler
  handleFormatChange(event) {
    const selectedFormat = event.target.value;
    
    if (!selectedFormat) return;
    
    switch (selectedFormat) {
      case 'heading1':
        this.editor.chain().focus().toggleHeading({ level: 1 }).run();
        break;
      case 'heading2':
        this.editor.chain().focus().toggleHeading({ level: 2 }).run();
        break;
      case 'blockquote':
        this.editor.chain().focus().toggleBlockquote().run();
        break;
      case 'bulletList':
        this.editor.chain().focus().toggleBulletList().run();
        break;
      case 'orderedList':
        this.editor.chain().focus().toggleOrderedList().run();
        break;
      case 'paragraph':
        this.editor.chain().focus().setParagraph().run();
        break;
    }
  }

  handleFileUpload(event) {
    const file = event.target.files[0];
    if (!file) return;
    
    this.uploadFile(file, event.target.name === "image");
    
    // Reset the file input
    event.target.value = '';
  }

  // Upload a file (image or attachment) to the server
  uploadFile(file, isImage = true, onSuccess = null) {
    // Create form data for upload
    const formData = new FormData();
    formData.append("file", file);
    
    // Show external upload indicator
    const uploadIndicator = this.createUploadIndicator(file.name);
    document.body.appendChild(uploadIndicator);
    
    // Temporarily disable editor during upload
    this.editor.setEditable(false);
    
    // Upload the image
    const endpoint = isImage ? '/dashboard/shared/image_uploads' : '/dashboard/shared/attachments';
    return fetch(endpoint, {
      method: 'POST',
      body: formData,
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
        'Accept': 'application/json'
      },
      credentials: 'include'
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Upload failed');
      }
      return response.json();
    })
    .then(data => {
      if (onSuccess) {
        onSuccess(data);
      } else if (isImage) {
        // Insert the image at the current position
        this.editor.chain().focus().setImage({ src: data.url }).run();
      } else {
        // Insert the attachment as a link
        const { selection } = this.editor.state;
        const hasContent = !selection.empty;

        if (hasContent) {
          // If text is selected, turn it into a link to the attachment
          this.editor.chain().focus().extendMarkRange('link').setLink({ href: data.href }).run();
        } else {
          // If no text is selected, insert the filename as a link
          this.editor.chain().focus().insertContent({
            type: 'text',
            marks: [{ type: 'link', attrs: { href: data.href } }],
            text: file.name
          }).run();
        }
      }

      // Re-enable editor
      this.editor.setEditable(true);

      // Move cursor to new paragraph inserted after image
      this.editor.commands.focus();

      // Remove the upload indicator
      document.body.removeChild(uploadIndicator);

      return data;
    })
    .catch(error => {
      console.error('Upload error:', error);
      
      // Show error in the indicator
      uploadIndicator.querySelector('.upload-status').textContent = 'Upload failed';
      uploadIndicator.querySelector('.upload-indicator-progress').style.backgroundColor = '#f87171';
      
      // Add a close button
      const closeButton = document.createElement('button');
      closeButton.textContent = '×';
      closeButton.className = 'upload-indicator-close';
      closeButton.addEventListener('click', () => {
        document.body.removeChild(uploadIndicator);
        this.editor.setEditable(true);
      });
      uploadIndicator.appendChild(closeButton);
      
      throw error;
    });
  }
}
